import React, {
  useState,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { useFormik } from 'formik';
import { AccountBase } from '@/models/AccountViewModel';
import StyledTextField from '@/components/common/styledTextField';
import StyledSelect from '@/components/common/styledSelect';
import AccountTagsAutocomplete from '@/components/common/AccountTagsAutocomplete';
import ZillowLogo from '@/assets/images/zillow-logo.png';
import { useGetClassificationsQuery } from '@/store/classificationsApi';
import {
  useUpdateAccountTagsMutation,
  useUpdateOfflineAccountMutation,
  useGetOfflineAccountQuery,
  OfflineAccount,
} from '@/store/accountsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import CustomCurrencyInput from '@/components/common/CustomCurrencyInput';
import { useLocalization } from '@/hooks/useLocalization';
import { useAccountTagCatalogOptions } from '@/hooks/useAccountTagCatalogOptions';
import { useAttributeAccountsToViewingTag } from '@/hooks/useAttributeAccountsToViewingTag';
import { normalizeSubClassificationId } from '@/modules/portfolio/accountModal/categories.utility';
import {
  ClassificationIdNumber,
  ClassificationIds,
  OFFLINE_ACCOUNT_UNSET_CLASSIFICATION_IDS,
  SubClassificationIdNumber,
  WealthAccountTransactionTypeIdNumber,
  WealthAccountTransactionTypeIds,
} from '@/utils/enums';
import { deriveIsAssetFromAccount } from './accountClassification';
import { useOfflineAccountResyncOnModalOpen } from '@/modules/portfolio/dialogs/hooks/useOfflineAccountResyncOnModalOpen';
import {
  ROOT_CATEGORY_TAG_KEY,
  deriveSelectedInvestorTagIdsFromAccount,
  tagIdsForAccountSave,
  type AccountGroupTagOption,
} from '@/utils/accountGroupTagAssignments';

/** Form values for InformationTab, aligned with API and UI fields */
interface InformationTabFormValues {
  Name: string;
  AccountNumber: string;
  Balance: string;
  IsAsset: boolean;
  ClassificationId: number;
  SubClassificationId: number;
  TransactionTypeId: number;
  Memo: string;
  MonthlyPaymentAmount: string;
  InitialLoanAmount: string;
  InterestRate: number;
  LoanBalance: string;
  Payment: string;
}

export interface InformationTabProps {
  account: AccountBase & {
    MonthlyPaymentAmount?: number;
    InitialLoanAmount?: number;
    InterestRate?: number;
    LoanBalance?: number;
    Payment?: number;
    TopClassificationId?: number;
  };
  onClose?: () => void;
  /** When false→true, refetch offline account and re-hydrate from API. */
  modalOpen: boolean;
  /**
   * Optional callback to notify parent component whenever the overall validity of this tab changes.
   * Mirrors the behavior of canSubmit / isFormDetailsValid from the legacy implementation.
   */
  onValidationChange?: (valid: boolean) => void;
  /**
   * Optional callback to notify parent component whenever the loading state of this tab changes.
   */
  onLoadingChange?: (loading: boolean) => void;
}

export interface InformationTabHandle {
  submit: () => Promise<void>;
  /** Returns the payload that would be sent (after validation). Used by modal for combined offline save. */
  getPayload: () => Promise<Record<string, unknown>>;
  /** Whether the form has unsaved changes (dirty). Used to prompt before closing. */
  hasChanges: () => boolean;
  /**
   * Writes personal/firm tag membership after the account save. Call this when
   * the account PUT happens outside `submit` (offline combined save).
   */
  syncTagMembership: () => Promise<void>;
}

function tagsFingerprint(ids: number[]): string {
  return [...ids].sort((a, b) => a - b).join(',');
}

function canIncludeTagUpdates(
  selectedTagIds: number[],
  baselineFingerprint: string,
  allTags: AccountGroupTagOption[],
  wasEdited = false,
): boolean {
  return (
    allTags.length > 0 &&
    (wasEdited || tagsFingerprint(selectedTagIds) !== baselineFingerprint)
  );
}

const InformationTab = forwardRef<InformationTabHandle, InformationTabProps>(
  (
    {
      account,
      modalOpen,
      onClose: _onClose,
      onValidationChange,
      onLoadingChange,
    },
    ref,
  ) => {
    const isOfflineAccount = account.AccountType === 2;
    const { t } = useLocalization();

    const { data: classificationsResponse } = useGetClassificationsQuery();
    const classifications = useMemo(
      () => classificationsResponse?.Records ?? [],
      [classificationsResponse?.Records],
    );

    const {
      options: allTags,
      userTagIdSet: genericTagIdSet,
      isLoading: isLoadingTagCatalog,
      refetchUserTags,
    } = useAccountTagCatalogOptions();

    useEffect(() => {
      if (!modalOpen) return;
      refetchUserTags();
    }, [modalOpen, refetchUserTags]);

    const [
      updateOnlineAccount,
      { isLoading: isUpdatingOnline, error: updateOnlineError },
    ] = useUpdateAccountTagsMutation();
    const [
      updateOfflineAccount,
      { isLoading: isUpdatingOffline, error: updateOfflineError },
    ] = useUpdateOfflineAccountMutation();

    const isUpdating = isOfflineAccount ? isUpdatingOffline : isUpdatingOnline;
    const updateError = isOfflineAccount
      ? updateOfflineError
      : updateOnlineError;

    // If offline, fetch the full offline account details
    const { data: offlineAccountData, isFetching: isFetchingOffline } =
      useGetOfflineAccountQuery(
        isOfflineAccount ? account.AccountId : skipToken,
      );

    // Calculate overall loading state
    const isLoading = (isOfflineAccount && isFetchingOffline) || isUpdating;

    // Notify parent of loading state changes
    React.useEffect(() => {
      onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    // Merge base account with offline details when fetched
    const fullAccount =
      isOfflineAccount && offlineAccountData ? offlineAccountData : account;

    // Sync from API to form only once per modal open session.
    // Re-running on every offlineAccountData change (e.g. RTK Query refetch)
    // would overwrite user edits and make the form appear non-editable.
    const hasInitializedFromOfflineData = React.useRef(false);

    useOfflineAccountResyncOnModalOpen(
      modalOpen,
      account.AccountId,
      isOfflineAccount,
      hasInitializedFromOfflineData,
    );
    const initialValues: InformationTabFormValues = useMemo(
      () => ({
        Name: fullAccount.Name,
        AccountNumber: fullAccount.AccountNumber || '',
        Balance: String(fullAccount.Balance || 0),
        IsAsset: deriveIsAssetFromAccount(fullAccount),
        ClassificationId: fullAccount.ClassificationId,
        SubClassificationId: fullAccount.SubClassificationId,
        TransactionTypeId: fullAccount.TransactionTypeId,
        Memo: fullAccount.Memo || '',
        MonthlyPaymentAmount: String(fullAccount.MonthlyPaymentAmount || 0),
        InitialLoanAmount: String(fullAccount.InitialLoanAmount || 0),
        InterestRate: fullAccount.InterestRate || 0,
        LoanBalance: String(fullAccount.LoanBalance || 0),
        Payment: String(fullAccount.Payment || 0),
      }),
      [fullAccount],
    );

    const [selectedTags, setSelectedTags] = useState<number[]>([]);
    const selectedTagsRef = useRef<number[]>([]);
    const tagsWereEditedRef = useRef(false);
    const hasInitializedTagsRef = useRef(false);
    const baselineTagsFingerprintRef = useRef<string>('');
    const baselineTagIdsRef = useRef<number[]>([]);
    const syncTagMembershipRef = useRef<() => Promise<void>>(async () => {});
    const { syncAccountTagMembership } = useAttributeAccountsToViewingTag();

    useOfflineAccountResyncOnModalOpen(
      modalOpen,
      account.AccountId,
      false,
      hasInitializedTagsRef,
      { resetOnModalOpen: true },
    );

    useEffect(() => {
      if (modalOpen) {
        tagsWereEditedRef.current = false;
      }
    }, [modalOpen]);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const formik = useFormik<InformationTabFormValues>({
      initialValues,
      validate: (values) => {
        const errors: Partial<Record<keyof InformationTabFormValues, string>> =
          {};
        if (!values.Name.trim()) errors.Name = 'Required';
        if (!values.ClassificationId) errors.ClassificationId = 'Required';
        const realEstate =
          values.ClassificationId === ClassificationIdNumber.REAL_ESTATE;
        if (
          isOfflineAccount &&
          realEstate &&
          isNaN(parseFloat(values.Balance))
        ) {
          errors.Balance = 'Invalid number';
        }
        return errors;
      },
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit: async (values) => {
        setSuccessMessage('');
        setErrorMessage('');
        try {
          const didIncludeTagUpdates = canIncludeTagUpdates(
            selectedTagsRef.current,
            baselineTagsFingerprintRef.current,
            allTags,
            tagsWereEditedRef.current,
          );
          const payload = buildPayload(values);
          if (isOfflineAccount) {
            await updateOfflineAccount(
              payload as unknown as OfflineAccount,
            ).unwrap();
          } else {
            await updateOnlineAccount(
              payload as unknown as AccountBase,
            ).unwrap();
          }
          if (didIncludeTagUpdates) {
            await syncTagMembershipRef.current();
          }
          setSuccessMessage('Account updated successfully');
          // Modal closes after handleSave completes; do not close here
        } catch (error: unknown) {
          if (
            error instanceof Error &&
            error.name === 'TagMembershipSyncError'
          ) {
            setErrorMessage(error.message);
          } else {
            const err = error as { data?: { message?: string } };
            setErrorMessage(err.data?.message ?? 'Failed to update account');
          }
          throw error;
        }
      },
    });

    useEffect(() => {
      if (
        isOfflineAccount &&
        offlineAccountData &&
        !hasInitializedFromOfflineData.current
      ) {
        hasInitializedFromOfflineData.current = true;
        formik.resetForm({
          values: {
            Name: offlineAccountData.Name,
            AccountNumber: offlineAccountData.AccountNumber || '',
            Memo: offlineAccountData.Memo || '',
            IsAsset: deriveIsAssetFromAccount(offlineAccountData),
            ClassificationId:
              offlineAccountData.ClassificationId ||
              OFFLINE_ACCOUNT_UNSET_CLASSIFICATION_IDS.primary,
            SubClassificationId:
              offlineAccountData.SubClassificationId ||
              OFFLINE_ACCOUNT_UNSET_CLASSIFICATION_IDS.sub,
            TransactionTypeId:
              offlineAccountData.TransactionTypeId ||
              WealthAccountTransactionTypeIds.CASH_FLOW,
            Balance: String(
              offlineAccountData.PropertyValue ?? offlineAccountData.Balance,
            ),
            MonthlyPaymentAmount: String(
              offlineAccountData.MonthlyPaymentAmount ?? 0,
            ),
            InitialLoanAmount: String(
              offlineAccountData.InitialLoanAmount ?? 0,
            ),
            InterestRate: offlineAccountData.InterestRate ?? 0,
            LoanBalance: String(offlineAccountData.LoanBalance ?? 0),
            Payment: String(offlineAccountData.Payment ?? 0),
          },
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOfflineAccount, offlineAccountData]);

    useEffect(() => {
      onValidationChange?.(formik.isValid);
    }, [formik.isValid, onValidationChange]);

    const balanceSheetCategories = useMemo(() => {
      const parentId = formik.values.IsAsset
        ? ClassificationIdNumber.ASSET
        : ClassificationIdNumber.LIABILITY;
      return classifications
        .filter((c) => c.ParentId === parentId)
        .map((c) => ({ value: c.ClassificationId, label: c.Name || '' }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [classifications, formik.values.IsAsset]);

    const secondaryCategories = useMemo(
      () =>
        classifications
          .filter((c) => c.ParentId === formik.values.ClassificationId)
          .map((c) => ({ value: c.ClassificationId, label: c.Name || '' }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      [classifications, formik.values.ClassificationId],
    );

    // Align subtype with the canonical classification tree from GET .../classifications
    // (fixes legacy/API payloads that store the parent id as SubClassificationId, etc.).
    useEffect(() => {
      if (!classifications.length) return;
      const next = normalizeSubClassificationId(
        classifications,
        formik.values.ClassificationId,
        formik.values.SubClassificationId,
      );
      if (next !== formik.values.SubClassificationId) {
        void formik.setFieldValue('SubClassificationId', next, false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- omit formik; only react to classification tree + ids
    }, [
      classifications,
      formik.values.ClassificationId,
      formik.values.SubClassificationId,
    ]);

    const prevTagOptionCountRef = useRef(0);
    useEffect(() => {
      if (!modalOpen) {
        prevTagOptionCountRef.current = 0;
        return;
      }

      const count = allTags.length;
      if (
        hasInitializedTagsRef.current &&
        !tagsWereEditedRef.current &&
        count > prevTagOptionCountRef.current
      ) {
        hasInitializedTagsRef.current = false;
      }
      prevTagOptionCountRef.current = count;
    }, [modalOpen, allTags.length]);

    useEffect(() => {
      if (!modalOpen) {
        return;
      }

      if (tagsWereEditedRef.current || hasInitializedTagsRef.current) {
        return;
      }

      if (!allTags.length) {
        selectedTagsRef.current = [];
        setSelectedTags([]);
        baselineTagsFingerprintRef.current = '';
        baselineTagIdsRef.current = [];
        return;
      }

      if (isOfflineAccount && !offlineAccountData) {
        return;
      }

      const sourceTagIds: number[] | undefined =
        isOfflineAccount && offlineAccountData?.TagIds
          ? offlineAccountData.TagIds
          : account.TagIds;
      const ids = deriveSelectedInvestorTagIdsFromAccount(
        sourceTagIds,
        allTags,
      );
      selectedTagsRef.current = ids;
      setSelectedTags(ids);
      baselineTagsFingerprintRef.current = tagsFingerprint(ids);
      baselineTagIdsRef.current = ids;
      hasInitializedTagsRef.current = true;
    }, [
      modalOpen,
      account.AccountId,
      account.TagIds,
      offlineAccountData,
      allTags,
      isOfflineAccount,
    ]);

    const buildPayload = useCallback(
      (values: InformationTabFormValues): Record<string, unknown> => {
        const baseAccount = isOfflineAccount ? offlineAccountData : account;
        if (!baseAccount?.AccountId) {
          throw new Error('Account ID is missing');
        }
        const isRealEstateVal =
          values.ClassificationId === ClassificationIdNumber.REAL_ESTATE;
        const isLiabilityVal = !values.IsAsset;
        const selectedTagIds = selectedTagsRef.current;
        const shouldIncludeTagUpdates = canIncludeTagUpdates(
          selectedTagIds,
          baselineTagsFingerprintRef.current,
          allTags,
          tagsWereEditedRef.current,
        );
        const updatedAccount: Record<string, unknown> = {
          ...baseAccount,
          AccountId: baseAccount.AccountId,
          AccountType: baseAccount.AccountType,
          Name: values.Name,
          AccountNumber: values.AccountNumber,
          Balance: parseFloat(values.Balance) || 0,
          PropertyValue:
            isOfflineAccount && isRealEstateVal
              ? parseFloat(values.Balance) || 0
              : undefined,
          IsAsset: values.IsAsset,
          ClassificationId:
            values.ClassificationId ||
            OFFLINE_ACCOUNT_UNSET_CLASSIFICATION_IDS.primary,
          SubClassificationId:
            values.SubClassificationId ||
            OFFLINE_ACCOUNT_UNSET_CLASSIFICATION_IDS.sub,
          TransactionTypeId: values.TransactionTypeId,
          TopClassificationId: values.IsAsset
            ? ClassificationIds.ASSET
            : ClassificationIds.LIABILITY,
          Memo: values.Memo,
          LastUpdated: new Date().toISOString(),
          MonthlyPaymentAmount: parseFloat(values.MonthlyPaymentAmount) || 0,
          InitialLoanAmount: parseFloat(values.InitialLoanAmount) || 0,
          InterestRate: values.InterestRate,
          LoanBalance:
            isOfflineAccount &&
            (isLiabilityVal || isRealEstateVal) &&
            values.LoanBalance
              ? parseFloat(values.LoanBalance) || 0
              : undefined,
          Payment:
            isOfflineAccount && isLiabilityVal && values.Payment
              ? parseFloat(values.Payment) || 0
              : undefined,
          TransactionTypeName:
            values.TransactionTypeId ===
            WealthAccountTransactionTypeIdNumber.CASH_FLOW
              ? 'Cash Flow'
              : 'Investment Activity',
        };
        /*
         * Build the save payload from updatedAccount, omitting fields the API
         * rejects or that are added separately below:
         * - always drop `id`
         * - for linked accounts, drop `TopClassificationId`
         * - drop stale root-category tag keys from the spread account
         * Entitlement membership is not applied by the account PUT. TagIds are
         * still sent so unassignable ids stay on the record, then membership
         * is synced with PUT /api/user/tags/{id}. The Tags map is left
         * unchanged: a numeric key can be an account-group id that collides
         * with an entitlement id.
         */
        const payload = Object.fromEntries(
          Object.entries(updatedAccount).filter(([key]) => {
            if (key === 'id') return false;
            if (!isOfflineAccount && key === 'TopClassificationId') {
              return false;
            }
            if (ROOT_CATEGORY_TAG_KEY.test(key)) {
              return false;
            }
            return true;
          }),
        ) as Record<string, unknown>;
        if (shouldIncludeTagUpdates) {
          if (genericTagIdSet.size > 0) {
            payload.TagIds = tagIdsForAccountSave(
              selectedTagIds,
              genericTagIdSet,
              baseAccount.TagIds,
            );
          }
        }
        return payload;
      },
      [isOfflineAccount, offlineAccountData, account, allTags, genericTagIdSet],
    );

    const syncTagMembership = useCallback(async (): Promise<void> => {
      const baseAccount = isOfflineAccount ? offlineAccountData : account;
      const accountId = baseAccount?.AccountId;
      if (accountId == null) {
        return;
      }
      if (
        !canIncludeTagUpdates(
          selectedTagsRef.current,
          baselineTagsFingerprintRef.current,
          allTags,
          tagsWereEditedRef.current,
        )
      ) {
        return;
      }
      try {
        await syncAccountTagMembership(
          accountId,
          baselineTagIdsRef.current,
          selectedTagsRef.current,
        );
      } catch (error) {
        const syncError = new Error(t('editAccountModal.tagSyncError'), {
          cause: error,
        });
        syncError.name = 'TagMembershipSyncError';
        setErrorMessage(syncError.message);
        throw syncError;
      }
      baselineTagIdsRef.current = [...selectedTagsRef.current];
      baselineTagsFingerprintRef.current = tagsFingerprint(
        selectedTagsRef.current,
      );
      tagsWereEditedRef.current = false;
    }, [
      account,
      allTags,
      isOfflineAccount,
      offlineAccountData,
      syncAccountTagMembership,
      t,
    ]);
    syncTagMembershipRef.current = syncTagMembership;

    const isRealEstate =
      formik.values.ClassificationId === ClassificationIdNumber.REAL_ESTATE;
    const isLoan =
      !formik.values.IsAsset &&
      formik.values.SubClassificationId !== SubClassificationIdNumber.MORTGAGE;
    const isLiability = !formik.values.IsAsset;

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          await formik.submitForm();
          if (Object.keys(formik.errors).length > 0) {
            throw new Error('Validation failed');
          }
        },
        getPayload: async () => {
          const errors = await formik.validateForm();
          if (Object.keys(errors).length > 0) {
            void formik.setTouched(
              Object.fromEntries(Object.keys(errors).map((k) => [k, true])),
            );
            throw new Error('Validation failed');
          }
          return buildPayload(formik.values);
        },
        hasChanges: () =>
          formik.dirty ||
          tagsFingerprint(selectedTagsRef.current) !==
            baselineTagsFingerprintRef.current,
        syncTagMembership,
      }),
      [formik, buildPayload, syncTagMembership],
    );

    return (
      <Box
        component='form'
        onSubmit={formik.handleSubmit}
      >
        {isOfflineAccount && isFetchingOffline && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {successMessage && (
          <Alert
            severity='success'
            sx={{ mb: 3 }}
          >
            {successMessage}
          </Alert>
        )}

        {(errorMessage || updateError) && (
          <Alert
            severity='error'
            sx={{ mb: 3 }}
          >
            {errorMessage || 'An error occurred while updating the account'}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <StyledTextField
            label='Name'
            name='Name'
            value={formik.values.Name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.Name && Boolean(formik.errors.Name)}
            helperText={formik.touched.Name && formik.errors.Name}
            fullWidth
            required
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
        {isOfflineAccount && (
          <Box sx={{ mb: 3 }}>
            <StyledTextField
              label='Account Number (Optional)'
              name='AccountNumber'
              value={formik.values.AccountNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        )}

        {isOfflineAccount && !isRealEstate && (
          <Box sx={{ mb: 3 }}>
            <CustomCurrencyInput
              label='Balance'
              value={formik.values.Balance}
              onValueChange={(value) => {
                void formik.setFieldValue(
                  'Balance',
                  value === undefined ? '' : value,
                );
              }}
              prefix='$'
              decimalsLimit={2}
              allowDecimals
              allowNegativeValue
            />
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <FormControl component='fieldset'>
            <FormLabel
              component='legend'
              required
            >
              Type
            </FormLabel>
            <RadioGroup
              row
              value={formik.values.IsAsset ? 'asset' : 'liability'}
              onChange={(e) => {
                const isAsset = e.target.value === 'asset';
                void formik.setFieldValue('IsAsset', isAsset);
                void formik.setFieldValue(
                  'ClassificationId',
                  OFFLINE_ACCOUNT_UNSET_CLASSIFICATION_IDS.primary,
                );
                void formik.setFieldValue(
                  'SubClassificationId',
                  OFFLINE_ACCOUNT_UNSET_CLASSIFICATION_IDS.sub,
                );
              }}
            >
              <FormControlLabel
                value='asset'
                control={<Radio />}
                label='Asset'
              />
              <FormControlLabel
                value='liability'
                control={<Radio />}
                label='Liability'
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <StyledSelect
            label='Balance Sheet Category'
            options={balanceSheetCategories}
            value={formik.values.ClassificationId}
            onChange={(e) => {
              const val = e.target.value;
              const children = classifications.filter(
                (c) => c.ParentId === val,
              );
              void formik.setFieldValue('ClassificationId', val);
              void formik.setFieldValue(
                'SubClassificationId',
                children.length
                  ? children[0].ClassificationId
                  : OFFLINE_ACCOUNT_UNSET_CLASSIFICATION_IDS.sub,
              );
            }}
            fullWidth
          />
          {formik.touched.ClassificationId &&
            formik.errors.ClassificationId && (
              <Typography
                variant='caption'
                color='error'
                sx={{ mt: 0.5 }}
              >
                {formik.errors.ClassificationId}
              </Typography>
            )}
        </Box>

        {secondaryCategories.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <StyledSelect
              label='Secondary Category'
              options={secondaryCategories}
              value={formik.values.SubClassificationId}
              onChange={(e) => {
                void formik.setFieldValue(
                  'SubClassificationId',
                  e.target.value,
                );
              }}
              fullWidth
            />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <AccountTagsAutocomplete
            options={allTags}
            selectedTagIds={selectedTags}
            isLoading={isLoadingTagCatalog}
            disabled={!account.CanEdit || isLoadingTagCatalog}
            onChange={(ids) => {
              selectedTagsRef.current = ids;
              tagsWereEditedRef.current = true;
              setSelectedTags(ids);
            }}
          />
        </Box>

        {isLoan && (
          <Box>
            <Box sx={{ mb: 3 }}>
              <CustomCurrencyInput
                label='Monthly Payment Amount (Optional)'
                value={formik.values.MonthlyPaymentAmount}
                onValueChange={(value) => {
                  void formik.setFieldValue(
                    'MonthlyPaymentAmount',
                    value === undefined ? '' : value,
                  );
                }}
                prefix='$'
                decimalsLimit={2}
                allowDecimals
                fullWidth
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <CustomCurrencyInput
                label='Initial Loan Amount (Optional)'
                value={formik.values.InitialLoanAmount}
                onValueChange={(value) => {
                  void formik.setFieldValue(
                    'InitialLoanAmount',
                    value === undefined ? '' : value,
                  );
                }}
                prefix='$'
                decimalsLimit={2}
                allowDecimals
                fullWidth
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <StyledTextField
                label='Interest Rate (Optional)'
                name='InterestRate'
                value={formik.values.InterestRate.toString()}
                onChange={(e) => {
                  void formik.setFieldValue(
                    'InterestRate',
                    parseFloat(e.target.value) || 0,
                  );
                }}
                onBlur={formik.handleBlur}
                fullWidth
                slotProps={{
                  inputLabel: { shrink: true },
                  input: {
                    endAdornment: (
                      <InputAdornment position='end'>%</InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {isOfflineAccount && (isLiability || isRealEstate) && (
          <Box sx={{ mb: 3 }}>
            <CustomCurrencyInput
              label='Loan Balance'
              value={formik.values.LoanBalance}
              onValueChange={(value) => {
                void formik.setFieldValue(
                  'LoanBalance',
                  value === undefined ? '' : value,
                );
              }}
              prefix='$'
              decimalsLimit={2}
              allowDecimals
              fullWidth
            />
          </Box>
        )}

        {isOfflineAccount && isLiability && (
          <Box sx={{ mb: 3 }}>
            <CustomCurrencyInput
              label='Payment'
              value={formik.values.Payment}
              onValueChange={(value) => {
                void formik.setFieldValue(
                  'Payment',
                  value === undefined ? '' : value,
                );
              }}
              prefix='$'
              decimalsLimit={2}
              allowDecimals
              fullWidth
            />
          </Box>
        )}

        {isOfflineAccount && isRealEstate && (
          <>
            <Box sx={{ mb: 3 }}>
              <StyledTextField
                label='Address (Optional)'
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={<Checkbox defaultChecked />}
                  label="View current Zestimate 'as is' value"
                />
                <img
                  src={ZillowLogo}
                  alt='Zillow'
                  style={{
                    height: '24px',
                    marginLeft: '8px',
                    marginRight: '8px',
                  }}
                />
                <TextField
                  defaultValue='$ 0.'
                  sx={{ width: '150px' }}
                  disabled
                />
              </Box>
              <Typography
                variant='body2'
                sx={{ mt: 1 }}
              >
                See more details for{' '}
                <Link
                  href='#'
                  underline='always'
                >
                  {formik.values.Name}
                </Link>{' '}
                on Zillow.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 3 }}>
                  <CustomCurrencyInput
                    label='Home Value'
                    value={formik.values.Balance}
                    onValueChange={(value) => {
                      void formik.setFieldValue(
                        'Balance',
                        value === undefined ? '' : value,
                      );
                    }}
                    prefix='$'
                    decimalsLimit={2}
                    allowDecimals
                    allowNegativeValue
                    fullWidth
                  />
                  {formik.touched.Balance && formik.errors.Balance && (
                    <Typography
                      variant='caption'
                      color='error'
                      sx={{ mt: 0.5 }}
                    >
                      {formik.errors.Balance}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}

        <Box sx={{ mb: 3 }}>
          <StyledTextField
            label='Notes (Optional)'
            name='Memo'
            value={formik.values.Memo}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            multiline
            rows={4}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <FormControl component='fieldset'>
            <FormLabel
              component='legend'
              required
            >
              Transactions Type
            </FormLabel>
            <RadioGroup
              row
              value={formik.values.TransactionTypeId.toString()}
              onChange={(e) => {
                void formik.setFieldValue(
                  'TransactionTypeId',
                  parseInt(e.target.value, 10),
                );
              }}
            >
              <FormControlLabel
                value={String(WealthAccountTransactionTypeIds.CASH_FLOW)}
                control={<Radio />}
                label={t('Cash Flow')}
              />
              <FormControlLabel
                value={String(
                  WealthAccountTransactionTypeIds.INVESTMENT_ACTIVITY,
                )}
                control={<Radio />}
                label={t('Trading Activity')}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Action buttons are provided by the parent dialog */}
      </Box>
    );
  },
);

export default InformationTab;
